import type {
  AgentRuntimeActionAvailability,
  AgentRuntimeActionState,
  AgentRuntimeCapabilities,
  AgentRuntimeInteractionRequest,
  AgentRuntimeSnapshot,
  AgentRuntimeStatus,
  AgentRuntimeTimelineItem,
} from "@cord/shared"
import type {
  NormalizedRawSyncEvent,
  RawSyncEventBatchApplyResult,
} from "@/lib/runtime-sync-batch"
import { extractHiddenMessageLinkage } from "@/lib/session-context-transport"
import { extractRuntimeClientMessageId } from "@/lib/runtime-client-message-id"

type CursorEventEntry = {
  id: number
  eventType: string
  data: unknown
  timestamp: number
}

type PendingCursorQuestion = {
  kind: "question" | "plan"
  sessionId: string | null
  raw: Record<string, unknown>
}

export type CursorRawProjection = {
  events: CursorEventEntry[]
  pendingPermissionsById: Record<string, Record<string, unknown>>
  pendingQuestionsById: Record<string, PendingCursorQuestion>
  providerStatus: Record<string, unknown> | null
  providerSyncBoundaryId: number | null
  sessionId: string | null
  title: string | null
  mode: string | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizeTimestamp(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  const normalized = Math.floor(value)
  return normalized < 10_000_000_000 ? normalized * 1000 : normalized
}

function createAvailability(enabled: boolean, disabledReason: string | null): AgentRuntimeActionAvailability {
  return {
    available: true,
    enabled,
    disabledReason: enabled ? null : disabledReason,
  }
}

function normalizeToolStatus(value: unknown): "pending" | "running" | "completed" | "error" {
  const normalized = asString(value)?.toLowerCase()
  if (normalized === "pending") return "pending"
  if (normalized === "running" || normalized === "in_progress") return "running"
  if (normalized === "completed" || normalized === "done" || normalized === "succeeded") return "completed"
  if (normalized === "error" || normalized === "failed") return "error"
  return "running"
}

function stringifyUnknown(value: unknown): string {
  if (typeof value === "string") return value
  if (Array.isArray(value)) {
    return value.map((entry) => stringifyUnknown(entry)).filter(Boolean).join("\n")
  }
  const record = asRecord(value)
  if (record) {
    const directText = asString(record.text)
    if (directText) return directText
    const nestedContent = asRecord(record.content)
    if (nestedContent) return stringifyUnknown(nestedContent)
    try {
      return JSON.stringify(record, null, 2)
    } catch {
      return String(record)
    }
  }
  return ""
}

function extractChunkText(raw: unknown): string {
  const record = asRecord(raw)
  if (!record) return stringifyUnknown(raw)
  return stringifyUnknown(record.content ?? record)
}

function normalizeQuestionPrompts(raw: Record<string, unknown>, requestId: string) {
  const sourcePrompts = Array.isArray(raw.questions)
    ? raw.questions
    : Array.isArray(raw.prompts)
      ? raw.prompts
      : []

  const prompts = sourcePrompts
    .map((entry, index) => {
      const prompt = asRecord(entry)
      if (!prompt) return null
      const options = Array.isArray(prompt.options)
        ? prompt.options
            .map((option, optionIndex) => {
              const row = asRecord(option)
              const label = asString(row?.label) ?? asString(row?.name)
              if (!label) return null
              return {
                id: asString(row?.id) ?? `${requestId}:${index}:${optionIndex}`,
                label,
                description: asString(row?.description) ?? undefined,
              }
            })
            .filter((option): option is NonNullable<typeof option> => option !== null)
        : []
      return {
        id: asString(prompt.id) ?? `${requestId}:${index}`,
        prompt: asString(prompt.prompt) ?? asString(prompt.question) ?? "",
        header: asString(prompt.header) ?? asString(prompt.title) ?? undefined,
        allowMultiple: prompt.allowMultiple === true || prompt.multiple === true,
        allowCustom: prompt.allowCustom === true || prompt.custom === true,
        options,
      }
    })
    .filter((prompt): prompt is NonNullable<typeof prompt> => prompt !== null)

  if (prompts.length > 0) return prompts

  const fallbackPrompt = asString(raw.prompt) ?? asString(raw.title) ?? "Choose an option"
  const fallbackOptions = Array.isArray(raw.options)
    ? raw.options
        .map((option, index) => {
          const row = asRecord(option)
          const label = asString(row?.label) ?? asString(row?.name)
          if (!label) return null
          return {
            id: asString(row?.id) ?? `${requestId}:fallback:${index}`,
            label,
            description: asString(row?.description) ?? undefined,
          }
        })
        .filter((option): option is NonNullable<typeof option> => option !== null)
    : []

  return [
    {
      id: `${requestId}:fallback`,
      prompt: fallbackPrompt,
      allowMultiple: false,
      allowCustom: false,
      options: fallbackOptions,
    },
  ]
}

function buildPlanPrompts(raw: Record<string, unknown>, requestId: string) {
  const title = asString(raw.title) ?? asString(raw.prompt) ?? "Approve the generated plan?"
  const options = Array.isArray(raw.options)
    ? raw.options
        .map((option, index) => {
          const row = asRecord(option)
          const label = asString(row?.label) ?? asString(row?.name)
          if (!label) return null
          return {
            id: asString(row?.id) ?? `${requestId}:plan:${index}`,
            label,
            description: asString(row?.description) ?? undefined,
          }
        })
        .filter((option): option is NonNullable<typeof option> => option !== null)
    : [
        { id: `${requestId}:approve`, label: "Approve", description: "Proceed with the plan" },
        { id: `${requestId}:reject`, label: "Request changes", description: "Send the plan back for revision" },
      ]

  return {
    title,
    prompts: [
      {
        id: `${requestId}:plan`,
        prompt: title,
        allowMultiple: false,
        allowCustom: false,
        options,
      },
    ],
  }
}

function buildCursorTimeline(events: CursorEventEntry[]): AgentRuntimeTimelineItem[] {
  if (events.length === 0) return []

  const items: AgentRuntimeTimelineItem[] = []
  let currentStreamingId: string | null = null
  let currentStreamingKind: "assistant_text" | "thinking" | null = null
  let currentUserMessageId: string | null = null

  const finalizeStreaming = () => {
    if (!currentStreamingId) return
    const index = items.findIndex((item) => item.id === currentStreamingId)
    if (index >= 0) {
      const current = items[index]
      if (current.kind === "assistant_text" || current.kind === "thinking") {
        items[index] = {
          ...current,
          streaming: false,
        }
      }
    }
    currentStreamingId = null
    currentStreamingKind = null
  }

  const resetTurnState = () => {
    finalizeStreaming()
    currentUserMessageId = null
  }

  for (const [entryIndex, entry] of events.entries()) {
    const event = asRecord(entry.data)
    if (!event) continue

    if (entry.eventType === "run_completed" || entry.eventType === "run_error" || entry.eventType === "aborted") {
      resetTurnState()
      if (entry.eventType === "run_error") {
        items.push({
          id: `cursor:error:${entry.id}`,
          kind: "error",
          message: asString(event.error) ?? "Cursor run failed",
          timestamp: entry.timestamp,
        })
      }
      continue
    }

    if (entry.eventType === "todo_update") {
      resetTurnState()
      const raw = asRecord(event.raw) ?? {}
      items.push({
        id: `cursor:todo:${entry.id}`,
        kind: "tool_call",
        toolCallId: `cursor:todo:${entry.id}`,
        toolName: "todowrite",
        input: raw,
        metadata: raw,
        status: "completed",
        timestamp: entry.timestamp,
        providerRef: {
          agentType: "cursor",
          sessionId: asString(event.sessionId) ?? undefined,
        },
      })
      continue
    }

    if (entry.eventType === "task_update") {
      resetTurnState()
      const raw = asRecord(event.raw) ?? {}
      items.push({
        id: `cursor:task:${entry.id}`,
        kind: "tool_call",
        toolCallId: asString(raw.id) ?? `cursor:task:${entry.id}`,
        toolName: "task",
        input: raw,
        output: raw.result ?? raw.output,
        status: normalizeToolStatus(raw.status),
        timestamp: entry.timestamp,
        metadata: raw,
        providerRef: {
          agentType: "cursor",
          sessionId: asString(event.sessionId) ?? undefined,
          childSessionId: asString(raw.sessionId) ?? undefined,
        },
      })
      continue
    }

    if (entry.eventType === "image_generated") {
      resetTurnState()
      const raw = asRecord(event.raw) ?? {}
      items.push({
        id: `cursor:image:${entry.id}`,
        kind: "tool_call",
        toolCallId: `cursor:image:${entry.id}`,
        toolName: "generate_image",
        output: raw,
        status: "completed",
        timestamp: entry.timestamp,
        metadata: raw,
        providerRef: {
          agentType: "cursor",
          sessionId: asString(event.sessionId) ?? undefined,
        },
      })
      continue
    }

    if (
      entry.eventType === "permission_request" ||
      entry.eventType === "permission_reply" ||
      entry.eventType === "question_request" ||
      entry.eventType === "question_reply" ||
      entry.eventType === "question_reject" ||
      entry.eventType === "plan_request" ||
      entry.eventType === "plan_reply"
    ) {
      resetTurnState()
      continue
    }

    if (entry.eventType !== "session_update") {
      resetTurnState()
      continue
    }

    const update = asRecord(event.update)
    const sessionUpdate = asString(update?.sessionUpdate)
    if (!update || !sessionUpdate) continue

    if (sessionUpdate === "user_message_chunk") {
      const text = extractChunkText(update.content)
      if (!text) continue
      const extracted = extractHiddenMessageLinkage(text)
      const clientMessageId = extractRuntimeClientMessageId({ value: update })
      if (!currentUserMessageId) {
        currentUserMessageId = `cursor:user:${entry.id}`
        items.push({
          id: currentUserMessageId,
          kind: "user_message",
          clientMessageId: extracted.clientMessageId ?? clientMessageId,
          text: extracted.text,
          contextRefs: extracted.contextRefs.length > 0 ? extracted.contextRefs : undefined,
          requestedSkillRefs:
            extracted.requestedSkillRefs.length > 0 ? extracted.requestedSkillRefs : undefined,
          preferredMcpServerRefs:
            extracted.preferredMcpServerRefs.length > 0 ? extracted.preferredMcpServerRefs : undefined,
          attachedPullRequest: extracted.attachedPullRequest,
          timestamp: entry.timestamp,
          providerRef: {
            agentType: "cursor",
            sessionId: asString(event.sessionId) ?? undefined,
          },
        })
      } else {
        const index = items.findIndex((item) => item.id === currentUserMessageId)
        if (index >= 0) {
          const current = items[index]
          if (current.kind === "user_message") {
            const combined = extractHiddenMessageLinkage(current.text + text)
            items[index] = {
              ...current,
              clientMessageId: current.clientMessageId ?? combined.clientMessageId ?? clientMessageId,
              text: combined.text,
              contextRefs:
                combined.contextRefs.length > 0
                  ? combined.contextRefs
                  : current.contextRefs,
              requestedSkillRefs:
                combined.requestedSkillRefs.length > 0
                  ? combined.requestedSkillRefs
                  : current.requestedSkillRefs,
              preferredMcpServerRefs:
                combined.preferredMcpServerRefs.length > 0
                  ? combined.preferredMcpServerRefs
                  : current.preferredMcpServerRefs,
              attachedPullRequest: combined.attachedPullRequest ?? current.attachedPullRequest,
            }
          }
        }
      }
      continue
    }

    currentUserMessageId = null

    if (sessionUpdate === "agent_message_chunk" || sessionUpdate === "agent_thought_chunk") {
      const kind = sessionUpdate === "agent_thought_chunk" ? "thinking" : "assistant_text"
      const text = extractChunkText(update.content)
      if (!text) continue

      if (!currentStreamingId || currentStreamingKind !== kind) {
        currentStreamingId = `cursor:stream:${entry.id}`
        currentStreamingKind = kind
        items.push({
          id: currentStreamingId,
          kind,
          text,
          streaming: true,
          timestamp: entry.timestamp,
          providerRef: {
            agentType: "cursor",
            sessionId: asString(event.sessionId) ?? undefined,
          },
        })
      } else {
        const index = items.findIndex((item) => item.id === currentStreamingId)
        if (index >= 0) {
          const current = items[index]
          if (current.kind === kind) {
            items[index] = {
              ...current,
              text: current.text + text,
              streaming: true,
            }
          }
        }
      }
      continue
    }

    finalizeStreaming()

    if (sessionUpdate === "tool_call") {
      const toolCallId = asString(update.toolCallId) ?? `cursor:tool:${entry.id}`
      items.push({
        id: `cursor:tool:${toolCallId}`,
        kind: "tool_call",
        toolCallId,
        toolName: asString(update.title) ?? asString(update.kind) ?? "tool",
        input: update.input ?? update.arguments,
        status: normalizeToolStatus(update.status),
        timestamp: entry.timestamp,
        metadata: update,
        providerRef: {
          agentType: "cursor",
          sessionId: asString(event.sessionId) ?? undefined,
          toolCallId,
        },
      })
      continue
    }

    if (sessionUpdate === "tool_call_update") {
      const toolCallId = asString(update.toolCallId)
      if (!toolCallId) continue
      const index = items.findIndex((item) => item.kind === "tool_call" && item.toolCallId === toolCallId)
      const outputText = stringifyUnknown(update.content ?? update.output ?? asRecord(update.fields)?.content)
      const nextStatus = normalizeToolStatus(update.status ?? asRecord(update.fields)?.status)
      const nextError = asString(update.error) ?? (nextStatus === "error" ? outputText || "Tool failed" : null)
      if (index >= 0) {
        const current = items[index]
        if (current.kind === "tool_call") {
          items[index] = {
            ...current,
            output: outputText || current.output,
            error: nextError,
            status: nextStatus,
            metadata: {
              ...(current.metadata ?? {}),
              ...update,
            },
          }
        }
      } else {
        items.push({
          id: `cursor:tool:${toolCallId}`,
          kind: "tool_call",
          toolCallId,
          toolName: asString(update.title) ?? "tool",
          output: outputText || undefined,
          error: nextError,
          status: nextStatus,
          timestamp: entry.timestamp,
          metadata: update,
          providerRef: {
            agentType: "cursor",
            sessionId: asString(event.sessionId) ?? undefined,
            toolCallId,
          },
        })
      }
      continue
    }

    if (sessionUpdate === "plan") {
      items.push({
        id: `cursor:plan:${entry.id}`,
        kind: "marker",
        markerType: "status",
        label: asString(update.title) ?? `Plan updated (${entryIndex + 1})`,
        timestamp: entry.timestamp,
        metadata: update,
      })
    }
  }

  resetTurnState()

  return items
}

function buildPendingInteractions(
  projection: CursorRawProjection,
): AgentRuntimeInteractionRequest[] {
  const interactions: AgentRuntimeInteractionRequest[] = []

  for (const [requestId, permission] of Object.entries(projection.pendingPermissionsById)) {
    const toolCall = asRecord(permission.toolCall)
    interactions.push({
      kind: "permission",
      requestId,
      title: asString(toolCall?.title) ?? "Permission required",
      description: asString(toolCall?.kind) ?? undefined,
      toolName: asString(toolCall?.title) ?? asString(toolCall?.kind) ?? undefined,
      input: toolCall ?? permission,
      raw: permission,
      providerRef: {
        agentType: "cursor",
        sessionId: asString(permission.sessionId) ?? projection.sessionId ?? undefined,
        toolCallId: asString(toolCall?.toolCallId) ?? asString(toolCall?.tool_call_id) ?? undefined,
      },
    })
  }

  for (const [requestId, question] of Object.entries(projection.pendingQuestionsById)) {
    if (question.kind === "plan") {
      const plan = buildPlanPrompts(question.raw, requestId)
      interactions.push({
        kind: "question",
        requestId,
        title: plan.title,
        prompts: plan.prompts,
        raw: question.raw,
        providerRef: {
          agentType: "cursor",
          sessionId: question.sessionId ?? projection.sessionId ?? undefined,
        },
      })
      continue
    }

    interactions.push({
      kind: "question",
      requestId,
      title: asString(question.raw.title) ?? undefined,
      prompts: normalizeQuestionPrompts(question.raw, requestId),
      raw: question.raw,
      providerRef: {
        agentType: "cursor",
        sessionId: question.sessionId ?? projection.sessionId ?? undefined,
      },
    })
  }

  return interactions
}

function buildStatus(
  providerStatus: Record<string, unknown> | null,
  pendingInteractionCount: number,
): AgentRuntimeStatus {
  const rawType = asString(providerStatus?.type)?.toLowerCase() ?? "idle"
  let phase: AgentRuntimeStatus["phase"] = "idle"
  if (rawType === "error" || rawType === "failed") {
    phase = "error"
  } else if (rawType === "waiting_input" || pendingInteractionCount > 0) {
    phase = "waiting_input"
  } else if (rawType === "busy" || rawType === "running" || rawType === "working") {
    phase = "running"
  }

  return {
    phase,
    agentType: "cursor",
    startedAt: normalizeTimestamp(providerStatus?.startedAt),
    finishedAt: normalizeTimestamp(providerStatus?.finishedAt),
    finishReason:
      asString(providerStatus?.finishReason) ??
      (phase === "waiting_input" ? "waiting_input" : phase === "idle" ? "completed" : null),
    error:
      phase === "error"
        ? {
            type: "runtime_error",
            message: asString(providerStatus?.message) ?? "Cursor run failed",
          }
        : null,
  }
}

function buildActions(
  status: AgentRuntimeStatus,
  timeline: AgentRuntimeTimelineItem[],
  pendingInteractions: AgentRuntimeInteractionRequest[],
): AgentRuntimeActionState {
  const busy = status.phase === "running"
  const hasConversation = timeline.length > 0
  const hasPendingPermission = pendingInteractions.some((entry) => entry.kind === "permission")
  const hasPendingQuestion = pendingInteractions.some((entry) => entry.kind === "question")

  return {
    availability: {
      abort: createAvailability(busy, "Nothing to abort right now"),
      undo: createAvailability(false, "Undo is not available for Cursor sessions yet"),
      redo: createAvailability(false, "Redo is not available for Cursor sessions yet"),
      compact: createAvailability(false, "Compact is not available for Cursor sessions yet"),
      fork_message: createAvailability(false, hasConversation ? "Fork is not available for Cursor sessions yet" : "No conversation yet"),
      edit_message: createAvailability(false, hasConversation ? "Edit from message is not available for Cursor sessions yet" : "No conversation yet"),
      reply_permission: createAvailability(hasPendingPermission, "No pending permission request"),
      reply_question: createAvailability(hasPendingQuestion, "No pending question"),
      question_reject: createAvailability(hasPendingQuestion, "No pending question"),
    },
    revertMessageId: null,
  }
}

function buildCapabilities(): AgentRuntimeCapabilities {
  return {
    agentType: "cursor",
    supportsPromptParts: true,
    supportsImageAttachments: true,
    supportsSlashCommands: false,
    supportsSessionActions: false,
    supportsQuestions: true,
    supportsPermissions: true,
    supportsUsage: false,
    supportsForkFromMessage: false,
    supportsEditFromMessage: false,
    supportsSubagents: true,
  }
}

function cloneProjection(input: CursorRawProjection): CursorRawProjection {
  return {
    events: structuredClone(input.events),
    pendingPermissionsById: structuredClone(input.pendingPermissionsById),
    pendingQuestionsById: structuredClone(input.pendingQuestionsById),
    providerStatus: input.providerStatus ? structuredClone(input.providerStatus) : null,
    providerSyncBoundaryId: input.providerSyncBoundaryId,
    sessionId: input.sessionId,
    title: input.title,
    mode: input.mode,
  }
}

const CURSOR_INCREMENTAL_EVENT_TYPES = new Set([
  "session_initialized",
  "session_update",
  "run_started",
  "run_completed",
  "run_error",
  "permission_request",
  "permission_reply",
  "question_request",
  "question_reply",
  "question_reject",
  "plan_request",
  "plan_reply",
  "todo_update",
  "task_update",
  "image_generated",
  "aborted",
])

function applyIncrementalEvent(
  projection: CursorRawProjection,
  eventType: string,
  payload: Record<string, unknown>,
  eventId: number,
  timestamp: number,
): CursorRawProjection {
  const next = cloneProjection(projection)
  next.events.push({
    id: eventId,
    eventType,
    data: payload,
    timestamp,
  })

  switch (eventType) {
    case "session_initialized": {
      next.sessionId = asString(payload.sessionId) ?? next.sessionId
      break
    }
    case "run_started": {
      next.providerStatus = {
        type: "busy",
        startedAt: timestamp,
      }
      break
    }
    case "run_completed": {
      next.pendingPermissionsById = {}
      next.pendingQuestionsById = {}
      next.providerStatus = {
        type: "idle",
        finishedAt: timestamp,
        finishReason: asString(payload.stopReason) ?? "completed",
      }
      break
    }
    case "aborted": {
      next.pendingPermissionsById = {}
      next.pendingQuestionsById = {}
      next.providerStatus = {
        type: "idle",
        finishedAt: timestamp,
        finishReason: "aborted",
      }
      break
    }
    case "run_error": {
      next.pendingPermissionsById = {}
      next.pendingQuestionsById = {}
      next.providerStatus = {
        type: "error",
        finishedAt: timestamp,
        finishReason: "error",
        message: asString(payload.error) ?? "Cursor run failed",
      }
      break
    }
    case "permission_request": {
      const requestId = asString(payload.requestId)
      if (requestId) {
        next.pendingPermissionsById[requestId] = { ...payload }
      }
      break
    }
    case "permission_reply": {
      const requestId = asString(payload.requestId)
      if (requestId) {
        delete next.pendingPermissionsById[requestId]
      }
      break
    }
    case "question_request": {
      const requestId = asString(payload.requestId)
      if (requestId) {
        next.pendingQuestionsById[requestId] = {
          kind: "question",
          sessionId: asString(payload.sessionId),
          raw: asRecord(payload.raw) ?? {},
        }
      }
      break
    }
    case "plan_request": {
      const requestId = asString(payload.requestId)
      if (requestId) {
        next.pendingQuestionsById[requestId] = {
          kind: "plan",
          sessionId: asString(payload.sessionId),
          raw: asRecord(payload.raw) ?? {},
        }
      }
      break
    }
    case "question_reply":
    case "question_reject":
    case "plan_reply": {
      const requestId = asString(payload.requestId)
      if (requestId) {
        delete next.pendingQuestionsById[requestId]
      }
      break
    }
    case "session_update": {
      const update = asRecord(payload.update)
      if (update?.sessionUpdate === "session_info_update") {
        next.title = asString(update.title) ?? next.title
      }
      if (update?.sessionUpdate === "current_mode_update") {
        next.mode = asString(update.modeId) ?? next.mode
      }
      break
    }
    default:
      break
  }

  if (!next.sessionId) {
    next.sessionId = asString(payload.sessionId) ?? next.sessionId
  }

  return next
}

export function buildCursorSnapshot(
  projection: CursorRawProjection,
  eventId: number,
): AgentRuntimeSnapshot {
  const timeline = buildCursorTimeline(projection.events)
  const pendingInteractions = buildPendingInteractions(projection)
  const status = buildStatus(projection.providerStatus, pendingInteractions.length)

  return {
    timeline,
    pendingMessages: [],
    pendingInteractions,
    status,
    usage: {},
    actions: buildActions(status, timeline, pendingInteractions),
    activeEditOperation: null,
    conversationEpoch: 0,
    sessionActionOutcome: null,
    capabilities: buildCapabilities(),
    cursorState: {
      providerEventId: eventId,
      canonicalEventId: null,
      providerSyncBoundaryId: projection.providerSyncBoundaryId,
    },
  }
}

export function createEmptyCursorProjection(): CursorRawProjection {
  return {
    events: [],
    pendingPermissionsById: {},
    pendingQuestionsById: {},
    providerStatus: null,
    providerSyncBoundaryId: null,
    sessionId: null,
    title: null,
    mode: null,
  }
}

export function applyCursorRawSyncEvent(input: {
  projection: CursorRawProjection | null
  eventType: string
  payload: unknown
  eventId: number
  timestamp: number
}): { projection: CursorRawProjection; snapshot: AgentRuntimeSnapshot } | null {
  const eventId = Number.isFinite(input.eventId) && input.eventId > 0
    ? Math.floor(input.eventId)
    : 0
  if (eventId <= 0) return null

  if (!CURSOR_INCREMENTAL_EVENT_TYPES.has(input.eventType)) return null
  const payload = asRecord(input.payload)
  if (!payload) return null
  if (payload.agentType !== "cursor") return null

  const nextProjection = applyIncrementalEvent(
    input.projection ?? createEmptyCursorProjection(),
    input.eventType,
    payload,
    eventId,
    input.timestamp,
  )

  return {
    projection: nextProjection,
    snapshot: buildCursorSnapshot(nextProjection, eventId),
  }
}

export function applyCursorRawSyncEventsBatch(input: {
  projection: CursorRawProjection | null
  events: NormalizedRawSyncEvent[]
}): RawSyncEventBatchApplyResult<CursorRawProjection> | null {
  let nextProjection = input.projection ?? createEmptyCursorProjection()
  let appliedCount = 0
  let lastAppliedEventId = 0
  let lastAppliedTimestamp = 0

  for (const event of input.events) {
    const eventId = Number.isFinite(event.eventId) && event.eventId > 0
      ? Math.floor(event.eventId)
      : 0
    if (eventId <= 0) continue
    if (!CURSOR_INCREMENTAL_EVENT_TYPES.has(event.eventType)) continue

    const payload = asRecord(event.payload)
    if (!payload || payload.agentType !== "cursor") continue

    nextProjection = applyIncrementalEvent(
      nextProjection,
      event.eventType,
      payload,
      eventId,
      event.timestamp,
    )
    appliedCount += 1
    lastAppliedEventId = eventId
    lastAppliedTimestamp = event.timestamp
  }

  if (appliedCount === 0) return null

  return {
    projection: nextProjection,
    snapshot: buildCursorSnapshot(nextProjection, lastAppliedEventId),
    appliedCount,
    lastAppliedEventId,
    lastAppliedTimestamp,
  }
}
